import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext, usePatientQuery } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  AI_API_VERSION_V1,
  AI_API_VERSION_V2,
  type AiApiVersion,
  getAiServiceBaseUrl,
} from '@ctw/shared/utils/environments';
import { QUERY_KEY_AI_CHART_REVIEW_SUMMARY } from '@ctw/shared/utils/query-keys';
import { retryWithExponentialBackoff } from '@ctw/shared/utils/request';
import { useState } from 'react';
import type { ChartReviewResponse } from './types';
import { translateUnixBreaksToHtml } from './utils';

const MAX_RETRIES = 5;
const BASE_DELAY = 2000;

interface GetOrPutSummaryViaNewApiArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  upid: string;
  summaryType: string;
  aiApiVersion: AiApiVersion;
}

// get a summary via the new API, if not found, try generating a new summary
const getOrPutSummaryViaNewApi = async ({
  ctwFetch,
  requestContext,
  upid,
  summaryType,
  aiApiVersion,
}: GetOrPutSummaryViaNewApiArgs): Promise<Array<ChartReviewResponse>> => {
  const endpoint = `${getAiServiceBaseUrl({
    env: requestContext.env,
    version: aiApiVersion,
  })}/patients/${upid}/summary/${summaryType}`;

  // we use fetch directly here because we want to be able to handle the 404
  // and return an error summary
  const resp = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: 'GET',
  });

  if (resp.ok) {
    const data: Array<ChartReviewResponse> = await resp.json();
    return data.map((summary) => {
      summary.hpi = translateUnixBreaksToHtml(summary.hpi);
      return summary;
    });
  }

  // if the response is a 404, try generating a new summary
  if (resp.status === 404) {
    return await triggerSummaryGeneration({
      ctwFetch,
      requestContext,
      upid,
      summaryType,
      aiApiVersion,
      bryansFinalGiftToOurBlessedUi: true,
    });
  }

  throw new Error('Error fetching patient summary');
};

interface JustInTimeSummaryArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  upid: string;
  summaryType: string;
  aiApiVersion: AiApiVersion;
}

const justInTimeSummary = async ({
  ctwFetch,
  requestContext,
  upid,
  summaryType,
  aiApiVersion,
}: JustInTimeSummaryArgs): Promise<Array<ChartReviewResponse>> => {
  const endpoint = `${getAiServiceBaseUrl({ env: requestContext.env, version: aiApiVersion })}/patients/${upid}/summaries/${summaryType}`;

  const resp = await ctwFetch<ChartReviewResponse>(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: 'GET',
  });

  resp.data.hpi = translateUnixBreaksToHtml(resp.data.hpi);
  return [resp.data];
};

interface TriggerSummaryGenerationArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  upid: string;
  summaryType: string;
  aiApiVersion: AiApiVersion;
  bryansFinalGiftToOurBlessedUi: boolean;
}

const triggerSummaryGeneration = async ({
  ctwFetch,
  requestContext,
  upid,
  summaryType,
  aiApiVersion,
  bryansFinalGiftToOurBlessedUi,
}: TriggerSummaryGenerationArgs): Promise<Array<ChartReviewResponse>> => {
  const endpoint = `${getAiServiceBaseUrl({ env: requestContext.env, version: aiApiVersion })}/patients/${upid}/${bryansFinalGiftToOurBlessedUi ? 'summary' : 'summaries'}/${summaryType}`;

  const resp = await ctwFetch<Array<ChartReviewResponse>>(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: bryansFinalGiftToOurBlessedUi ? 'POST' : 'PUT',
  });

  // hack: for now just return the first summary. We'll be
  // returning an array of summaries soon.
  if (resp.data.length === 0) {
    return [ErrorSummary];
  }

  return resp.data.map((summary) => {
    summary.hpi = translateUnixBreaksToHtml(summary.hpi);
    return summary;
  });
};

export const ErrorSummary: ChartReviewResponse = {
  title: 'Error',
  hpi: 'An error occurred while fetching the summary',
  citations: [],
};

export const NotFoundSummary: ChartReviewResponse = {
  title: 'Not Found',
  hpi: 'The summary was not found. Please contact support',
  citations: [],
};

export const useSummariesForPatient = (summaryType: string | null) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const telemetry = useTelemetry();

  const { requestContext, ctwFetch } = useCTW();
  const { patient } = usePatientContext();
  const bryansFinalGiftToOurBlessedUi = useFeatureFlag('ctw-focused-summary');
  const aiApiVersion = useFeatureFlag('ctw-python-summarization')
    ? AI_API_VERSION_V2
    : AI_API_VERSION_V1;

  const {
    data: document,
    isLoading,
    error,
    refetch,
  } = usePatientQuery({
    queryId: QUERY_KEY_AI_CHART_REVIEW_SUMMARY,
    queryKey: [summaryType],
    queryFn: async ({ requestContext, patient }): Promise<Array<ChartReviewResponse>> => {
      if (!summaryType) {
        return [ErrorSummary];
      }
      try {
        const upid = patient.UPID ?? '';

        const resp = bryansFinalGiftToOurBlessedUi
          ? await getOrPutSummaryViaNewApi({
              ctwFetch,
              requestContext,
              upid,
              summaryType,
              aiApiVersion,
            })
          : await justInTimeSummary({ ctwFetch, requestContext, upid, summaryType, aiApiVersion });

        return resp;
      } catch (e) {
        telemetry.trackError({
          message: `Error fetching patient summary (in queryFn) for UPID: ${patient.UPID}, Type: ${summaryType}`,
          error: e as Error,
        });
        return [ErrorSummary];
      }
    },
  });

  const getSummaries = async (summaryType: string | null) => {
    if (!summaryType) {
      return;
    }
    setIsGenerating(true);
    try {
      const upid = patient.UPID || 'unknown-upid';

      if (bryansFinalGiftToOurBlessedUi) {
        await getOrPutSummaryViaNewApi({
          ctwFetch,
          requestContext,
          upid,
          summaryType,
          aiApiVersion,
        });
      } else {
        await justInTimeSummary({ ctwFetch, requestContext, upid, summaryType, aiApiVersion });
      }

      // Wait for the summary to be generated
      return retryWithExponentialBackoff(
        async () => {
          const resp = await refetch();
          return resp;
        },
        MAX_RETRIES,
        BASE_DELAY,
      );
    } catch (e) {
      telemetry.trackError({ message: 'Error generating patient summary', error: e });
      throw new Error('Error generating summary');
    } finally {
      setIsGenerating(false);
    }
  };

  const regenerateSummaries = async (summaryType: string | null) => {
    if (!summaryType) {
      return;
    }
    setIsGenerating(true);
    try {
      const upid = patient.UPID || 'unknown-upid';
      // Get the new summary directly from the POST response
      const newSummaries = await triggerSummaryGeneration({
        ctwFetch,
        requestContext,
        upid,
        summaryType,
        bryansFinalGiftToOurBlessedUi,
        aiApiVersion,
      });

      // Force a refetch to update the cache, but don't wait for it or use its results
      void refetch();

      // Return the already-generated summary from the POST
      return newSummaries;
    } catch (e) {
      telemetry.trackError({ message: 'Error regenerating patient summary', error: e });
      throw new Error('Error regenerating summary');
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    document,
    isLoading,
    isGenerating,
    error,
    getSummaries,
    regenerateSummaries,
  };
};
